import { useEffect, useState, useRef } from "react";

// Email validation regex
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// URL & Promotional Link Detection Pattern
const containsLinkOrPromo = (text) => {
  const urlPattern = /(https?:\/\/|www\.|\.com|\.net|\.org|\.io|\.xyz|\.biz|\.top|\.ru|\.info|t\.me\/|wa\.me\/|bit\.ly|tinyurl)/i;
  const promoPattern = /\b(casino|crypto|bitcoin|forex|viagra|whatsapp\s*group|telegram\s*group|earn\s*\$|make\s*money|free\s*bonus|slots|porn|sex)\b/i;
  return urlPattern.test(text) || promoPattern.test(text);
};

// Profanity / Inappropriate words filter
const containsProfanity = (text) => {
  const profanities = [
    "fuck", "shit", "bitch", "asshole", "bastard", "dick", "pussy", "cunt",
    "puta", "mierda", "cabron", "joder", "gilipollas", "coño", "maricon",
    "sharmouta", "kos", "ayre", "kes"
  ];
  const words = text.toLowerCase().split(/[\s,.\-_!?:;]+/);
  return profanities.some((badWord) => words.includes(badWord));
};

const ArticleComments = ({ articleType, articleKey, title, captchaToken = null }) => {
  const [comments, setComments] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", message: "", honeypot: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Bot detection: track when form was rendered
  const formMountTimeRef = useRef(Date.now());

  useEffect(() => {
    formMountTimeRef.current = Date.now();
  }, [articleKey]);

  // Fetch comments on mount
  useEffect(() => {
    const fetchComments = async () => {
      try {
        const response = await fetch(
          `/api/comments?article_type=${encodeURIComponent(articleType)}&article_key=${encodeURIComponent(articleKey)}`
        );
        if (!response.ok) {
          throw new Error(`Failed to fetch comments: ${response.statusText}`);
        }
        const data = await response.json();
        setComments(Array.isArray(data) ? data : []);
      } catch (err) {
        setComments([]);
      }
    };

    fetchComments();
  }, [articleType, articleKey]);

  const validateForm = () => {
    const errors = {};

    // 1. Bot Trap (Honeypot)
    if (form.honeypot) {
      errors.message = "Spam bot detected. Submission rejected.";
      setValidationErrors(errors);
      return false;
    }

    // 2. Bot Trap (Time to submit must be > 2.5 seconds)
    const timeTaken = (Date.now() - formMountTimeRef.current) / 1000;
    if (timeTaken < 2.5) {
      errors.message = "Submission was too fast. Please take a moment to review before submitting.";
      setValidationErrors(errors);
      return false;
    }

    // 3. Rate Limiting (60 seconds cooldown per browser)
    const lastCommentTime = localStorage.getItem("last_comment_submitted_at");
    if (lastCommentTime) {
      const secondsSinceLast = Math.floor((Date.now() - parseInt(lastCommentTime, 10)) / 1000);
      if (secondsSinceLast < 60) {
        const remaining = 60 - secondsSinceLast;
        errors.message = `You are posting too quickly. Please wait ${remaining} seconds before submitting another comment.`;
        setValidationErrors(errors);
        return false;
      }
    }

    // 4. Name Validation
    const name = form.name.trim();
    if (!name) {
      errors.name = "Name is required";
    } else if (name.length > 100) {
      errors.name = "Name must be 100 characters or less";
    } else if (containsLinkOrPromo(name)) {
      errors.name = "Links or promotional names are not allowed";
    }

    // 5. Email Validation
    const email = form.email.trim();
    if (!email) {
      errors.email = "Email is required";
    } else if (!isValidEmail(email)) {
      errors.email = "Please enter a valid email address";
    }

    // 6. Comment Content Validation & Spam/Profanity Checks
    const message = form.message.trim();
    if (!message) {
      errors.message = "Comment is required";
    } else if (message.length < 3) {
      errors.message = "Comment must be at least 3 characters";
    } else if (message.length > 2000) {
      errors.message = "Comment must be 2000 characters or less";
    } else if (containsLinkOrPromo(message)) {
      errors.message = "Links and promotional content are not allowed in comments.";
    } else if (containsProfanity(message)) {
      errors.message = "Please keep your comment respectful and free of offensive language.";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await fetch("/api/comments", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          message: form.message.trim(),
          article_type: articleType,
          article_key: articleKey,
          captcha_token: captchaToken,
        }),
      });

      // Save rate-limit timestamp
      localStorage.setItem("last_comment_submitted_at", Date.now().toString());

      if (response.ok) {
        try {
          const newComment = await response.json();
          if (newComment && typeof newComment === "object" && newComment.name) {
            setComments((prev) => [newComment, ...prev]);
          }
        } catch {
          // Ignored if response is not JSON
        }
      }

      setForm({ name: "", email: "", message: "", honeypot: "" });
      setSuccess(true);
      setValidationErrors({});

      setTimeout(() => setSuccess(false), 4000);
    } catch (err) {
      console.error("Error posting comment:", err);
      setError("Failed to post comment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      className="article-comments"
      style={{
        marginTop: 32,
        borderTop: "1px solid rgba(0, 0, 0, 0.08)",
        paddingTop: 28,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "end",
          flexWrap: "wrap",
          marginBottom: 18,
        }}
      >
        <div>
          <h3 style={{ marginBottom: 6 }}>Comments</h3>
          <p style={{ margin: 0, color: "#666" }}>
            Share your thoughts on this article.
          </p>
        </div>
        <span
          style={{
            borderRadius: 999,
            padding: "6px 10px",
            background: "#f5f7fb",
            color: "#334155",
            fontSize: 12,
          }}
        >
          {comments.length} comment{comments.length === 1 ? "" : "s"}
        </span>
      </div>

      <form
        onSubmit={handleSubmit}
        style={{
          background: "#fafafa",
          border: "1px solid rgba(0, 0, 0, 0.08)",
          borderRadius: 16,
          padding: 18,
        }}
      >
        {/* Hidden Honeypot Field for Bot Detection */}
        <input
          type="text"
          name="website_url_field"
          value={form.honeypot}
          onChange={(e) => setForm((prev) => ({ ...prev, honeypot: e.target.value }))}
          style={{ display: "none", position: "absolute", left: "-9999px" }}
          tabIndex="-1"
          autoComplete="off"
        />

        {error && (
          <div
            style={{
              marginBottom: 16,
              padding: 12,
              background: "#fee",
              border: "1px solid #fcc",
              borderRadius: 8,
              color: "#c33",
              fontSize: 14,
            }}
          >
            {error}
          </div>
        )}

        {success && (
          <div
            style={{
              marginBottom: 16,
              padding: 12,
              background: "#efe",
              border: "1px solid #cfc",
              borderRadius: 8,
              color: "#16794c",
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            <i className="fa fa-check-circle me-2"></i>
            Comment posted successfully! Thank you for your feedback.
          </div>
        )}

        <div className="row g-3">
          <div className="col-md-4">
            <label htmlFor="comment-name" style={{ display: "block", marginBottom: 6 }}>
              Name
            </label>
            <input
              id="comment-name"
              className="form-control"
              value={form.name}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, name: event.target.value }))
              }
              placeholder="Your name"
              disabled={loading}
              maxLength={100}
              style={validationErrors.name ? { borderColor: "#dc3545" } : {}}
            />
            {validationErrors.name && (
              <small style={{ color: "#dc3545", display: "block", marginTop: 4 }}>
                {validationErrors.name}
              </small>
            )}
          </div>
          <div className="col-md-8">
            <label htmlFor="comment-email" style={{ display: "block", marginBottom: 6 }}>
              Email
            </label>
            <input
              id="comment-email"
              type="email"
              className="form-control"
              value={form.email}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, email: event.target.value }))
              }
              placeholder="your.email@example.com"
              disabled={loading}
              maxLength={150}
              style={validationErrors.email ? { borderColor: "#dc3545" } : {}}
            />
            {validationErrors.email && (
              <small style={{ color: "#dc3545", display: "block", marginTop: 4 }}>
                {validationErrors.email}
              </small>
            )}
          </div>
        </div>

        <div className="row g-3" style={{ marginTop: 0 }}>
          <div className="col-12">
            <label htmlFor="comment-message" style={{ display: "block", marginBottom: 6 }}>
              Comment
            </label>
            <textarea
              id="comment-message"
              className="form-control"
              rows={4}
              value={form.message}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, message: event.target.value }))
              }
              placeholder="Write your comment here..."
              disabled={loading}
              maxLength={2000}
              style={validationErrors.message ? { borderColor: "#dc3545" } : {}}
            />
            {validationErrors.message && (
              <small style={{ color: "#dc3545", display: "block", marginTop: 4 }}>
                {validationErrors.message}
              </small>
            )}
            <small style={{ color: "#666", display: "block", marginTop: 4 }}>
              {form.message.length}/2000 characters (links and spam are automatically filtered)
            </small>
          </div>
        </div>

        <input type="hidden" name="captcha_token" value={captchaToken || ""} />

        <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
            style={{
              opacity: loading ? 0.6 : 1,
              cursor: loading ? "not-allowed" : "pointer",
            }}
          >
            {loading ? "Posting..." : "Post comment"}
          </button>
        </div>
      </form>

      <div style={{ marginTop: 22 }}>
        {comments.length === 0 ? (
          <p style={{ margin: 0, color: "#666" }}>Be the first to comment.</p>
        ) : (
          <div style={{ display: "grid", gap: 14 }}>
            {comments.map((comment, index) => (
              <article
                key={comment.id || `comment-${index}`}
                style={{
                  border: "1px solid rgba(0, 0, 0, 0.08)",
                  borderRadius: 16,
                  padding: 16,
                  background: "#fff",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    flexWrap: "wrap",
                    marginBottom: 8,
                  }}
                >
                  <strong>{comment.name}</strong>
                  <span style={{ color: "#666", fontSize: 12 }}>
                    {comment.createdAt ? new Date(comment.createdAt).toLocaleString() : "Just now"}
                  </span>
                </div>
                <p style={{ margin: 0, whiteSpace: "pre-wrap" }}>{comment.message}</p>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default ArticleComments;
